// What a patient note says about its videos, and what may be attached to one.
//
// A note carries video and nothing else: no image, no PDF, no office file. The
// video goes up through keyon's chunked upload first (video_upload.php, domain
// `note`), and SavePatientNote then names the finished uploads by uid. keyon
// returns each note's videos as `attachments` on the note row (GetAllNotes,
// GetNoteByID): uid-only items, so the browser never sees a storage address and
// reaches the bytes only through file.php, which authorizes every read again.
//
// The numbers the server owns (the size limit, the accepted types, how many
// videos a note may carry, whether a note takes one at all) come from GET
// video_upload.php?action=config and are passed in as `policy`; nothing here
// mirrors them.
import { isFileUid } from '@/utils/fileUrl';
import { getAttachmentKind } from '@/utils/messageAttachments';
import { validateVideoFile } from '@/utils/videoUploadPlan';

// A note was saved, but the response gives no sign that keyon read attachment_uids
// (an older replica, or an image rolled back, ignores the field and answers
// Inserted). The note is on the server; the video is not on it. The text and the
// uploaded video stay where they are, so a second Save can carry them, but that
// is a second note, and the sentence says so.
export const NOTE_VIDEO_NOT_ATTACHED_TEXT =
  'The note was saved, but the video was not attached to it. Your video is still uploaded and your text is still here: press Save to save the note again with the video (this adds a second note), or Cancel to discard it.';

// keyon inserted the note (it answered its note_id) but InsertPatientNotes'
// notification step did not report success, so msg is not 'Inserted'. The note
// must not be saved again: that would be a second note.
export const NOTE_NOT_NOTIFIED_TEXT = 'The note was saved, but the notification about it did not go out.';
// The same, after a sentence that already said the note was saved.
export const NOTE_NOT_NOTIFIED_EITHER_TEXT = 'The notification about the note did not go out either.';

// What a finished upload says on its chip in the popup: it is not sent anywhere
// yet, it goes with the note when the note is saved.
export const NOTE_VIDEO_READY_TEXT = 'Ready. It is attached to the note when you save it.';

export const NOTE_VIDEO_UPLOADING_TEXT =
  'A video is still uploading. Wait for it to finish, or cancel the upload, before saving the note.';

// keyon could not attach some of the videos the save named. It frees them
// (abandons the uploads) when it says so, so there is nothing to retry: the
// note is saved and the video has to be added to a new note.
export function noteVideoUnboundText(count) {
  const n = Number.isInteger(count) && count > 0 ? count : 1;
  return n === 1
    ? 'The note was saved, but 1 video could not be attached to it. Add the video again in a new note.'
    : `The note was saved, but ${n} videos could not be attached to it. Add the videos again in a new note.`;
}

function positiveInteger(value) {
  return Number.isSafeInteger(value) && value > 0 ? value : 0;
}

/**
 * Whether this environment takes a video on a note: GET
 * video_upload.php?action=config says enabled, says a note may reference one
 * (refs.note, which an older keyon does not send) and gives the limit per note.
 * Anything else - no answer, a failed call, a policy that lacks any of these -
 * is no video, and the note popup is exactly what it was before video existed.
 * @param {object|null|undefined} policy the config answer
 */
export function noteVideoOffered(policy) {
  return (
    !!policy &&
    typeof policy === 'object' &&
    policy.enabled === true &&
    !!policy.refs &&
    policy.refs.note === true &&
    (!Array.isArray(policy.domains) || policy.domains.includes('note')) &&
    positiveInteger(policy.videos_per_note) > 0
  );
}

/** How many videos one note may carry, as the server says; 0 where a note takes none. */
export function noteVideoLimit(policy) {
  return noteVideoOffered(policy) ? policy.videos_per_note : 0;
}

/**
 * The picker's accept string for a note: the server's own video types and
 * extensions and nothing else (a note is video only), never video/* (a webm or
 * an avi would then be offered that keyon refuses). Empty where a note takes none.
 */
export function noteVideoAccept(policy) {
  if (!noteVideoOffered(policy)) return '';
  const safe = (v) => typeof v === 'string' && /^[A-Za-z0-9.+/_-]+$/.test(v);
  const types = (Array.isArray(policy.mime_types) ? policy.mime_types : []).filter(safe);
  const extensions = (Array.isArray(policy.extensions) ? policy.extensions : [])
    .filter(safe)
    .map((e) => `.${e.replace(/^\./, '')}`);
  return [...types, ...extensions].join(',');
}

/**
 * Whether a picked file may go on a note, in words a person can act on. The
 * file itself is judged as a message's video is (type, size, empty); only the
 * count is the note's own.
 * @param {{name:string, type?:string, size:number}} file
 * @param {object|null} policy GET video_upload.php?action=config's answer
 * @param {{videosInNote?:number}} [context] how many videos the note already carries
 * @returns {{ok:true}|{ok:false, code:'unsupported_video'|'too_many_videos'|'video_too_large'|'empty_file', message:string}}
 */
export function validateNoteVideoFile(file, policy, { videosInNote = 0 } = {}) {
  if (!noteVideoOffered(policy)) {
    return { ok: false, code: 'unsupported_video', message: 'Videos cannot be attached to a note right now.' };
  }
  // The count is checked below, against the note's limit; this is the file's own checks.
  const verdict = validateVideoFile(file, { ...policy, videos_per_message: Number.MAX_SAFE_INTEGER }, { videosInMessage: 0 });
  if (!verdict.ok) return verdict;
  const limit = noteVideoLimit(policy);
  if (videosInNote >= limit) {
    return {
      ok: false,
      code: 'too_many_videos',
      message: limit === 1 ? 'Only one video can be attached to a note.' : `Up to ${limit} videos can be attached to a note.`,
    };
  }
  return { ok: true };
}

/**
 * The patient id a note is saved under, exactly as NotePopup's saveNote sends it
 * (patient_id, then psersonalID; not the row id and not the externId). The
 * upload names the patient by this same value, and keyon matches the two
 * exactly, so the two places must never compute it separately.
 */
export function notePatientId(patient) {
  if (!patient) return undefined;
  return patient.patient_id || patient.psersonalID;
}

/**
 * A note row's `attachments` as the items a page shows: [{ name, file_uid,
 * kind, scan_status, removed?, byte_size?, patient_visible? }]. keyon sends a
 * list (a JSON string is read too). Only these keys are carried, so a url, a
 * path or anything else an answer might hold is never read: an item with
 * neither a name nor a uid is nothing to show.
 *
 * `patient_visible` (a boolean) is keyon's word to staff about who may play the
 * video; a patient's list does not have the key and so gets no badge.
 * @param {object|null} note
 */
export function normalizeNoteAttachments(note) {
  if (!note || typeof note !== 'object') return [];

  let raw = note.attachments;
  if (typeof raw === 'string' && raw.trim()) {
    try {
      raw = JSON.parse(raw);
    } catch {
      raw = null;
    }
  }
  if (!Array.isArray(raw) || !raw.length) return [];

  return raw
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const fileUid = isFileUid(item.file_uid) ? item.file_uid : null;
      if (!item.name && !fileUid) return null;
      const kind = getAttachmentKind(item);
      const byteSize = kind === 'video' && Number.isSafeInteger(item.byte_size) && item.byte_size >= 0
        ? item.byte_size
        : null;
      return {
        name: item.name || 'attachment',
        file_uid: fileUid,
        kind,
        scan_status: item.scan_status || null,
        ...(item.removed === true ? { removed: true } : {}),
        ...(byteSize !== null ? { byte_size: byteSize } : {}),
        ...(typeof item.patient_visible === 'boolean' ? { patient_visible: item.patient_visible } : {}),
      };
    })
    .filter(Boolean);
}

/** How many videos a note row shows: the ones that were removed are not counted. */
export function noteVideoCount(note) {
  return normalizeNoteAttachments(note).filter((item) => item.removed !== true).length;
}

/** "1 video", "2 videos"; '' for none. */
export function videoCountLabel(count) {
  if (!Number.isInteger(count) || count < 1) return '';
  return count === 1 ? '1 video' : `${count} videos`;
}

/**
 * The badge a staff viewer sees on a video, from keyon's patient_visible. A
 * list without the key (a patient's) has none.
 * @returns {'Staff only'|'Visible to patient'|null}
 */
export function visibilityLabel(item) {
  if (!item || typeof item.patient_visible !== 'boolean') return null;
  return item.patient_visible ? 'Visible to patient' : 'Staff only';
}
